import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTenant } from '@/hooks/useTenant';
import { htmlToText, resolveStoreName } from '@/lib/display';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { parseBlockSchema } from '@/lib/themeBlocks';
import ThemeBlockRenderer from '@/components/feature/ThemeBlockRenderer';
import ProductsTag from '@/components/feature/template-tags/ProductsTag';
import ServicesTag from '@/components/feature/template-tags/ServicesTag';
import BlogPostsTag from '@/components/feature/template-tags/BlogPostsTag';
import PartnersTag from '@/components/feature/template-tags/PartnersTag';
import TeamTag from '@/components/feature/template-tags/TeamTag';
import PortfolioTag from '@/components/feature/template-tags/PortfolioTag';
import ContactFormTag from '@/components/feature/template-tags/ContactFormTag';
import BookingFormTag from '@/components/feature/template-tags/BookingFormTag';
import TestimonialsTag from '@/components/feature/template-tags/TestimonialsTag';
import StatsTag from '@/components/feature/template-tags/StatsTag';
import MarketplaceProductsTag from '@/components/feature/template-tags/MarketplaceProductsTag';
import MarketplaceSellersTag from '@/components/feature/template-tags/MarketplaceSellersTag';
import MarketplaceSearchTag from '@/components/feature/template-tags/MarketplaceSearchTag';
import MarketplaceCategoriesTag from '@/components/feature/template-tags/MarketplaceCategoriesTag';
import FeaturedProductsTag from '@/components/feature/template-tags/FeaturedProductsTag';
import FaqTag from '@/components/feature/template-tags/FaqTag';
import GalleryTag from '@/components/feature/template-tags/GalleryTag';
import HebergementsTag from '@/components/feature/template-tags/HebergementsTag';
import HebergementSearchTag from '@/components/feature/template-tags/HebergementSearchTag';
import EventsTag from '@/components/feature/template-tags/EventsTag';
import FormationsTag from '@/components/feature/template-tags/FormationsTag';
import CategoriesTag from '@/components/feature/template-tags/CategoriesTag';
import RestaurationTag from '@/components/feature/template-tags/RestaurationTag';
import DefaultFormTag from '@/components/feature/template-tags/DefaultFormTag';
import HeroBannersTag from '@/components/feature/template-tags/HeroBannersTag';

// ── Moteur de rendu des pages de thème ──────────────────────────────
// Le HTML du thème est injecté d'un seul bloc (donc les sections, grilles
// et conteneurs restent CORRECTEMENT imbriqués), et chaque balise {{bloc}}
// est remplacée par un emplacement vide dans lequel on monte le vrai
// composant React correspondant (via un portail, pour préserver le contexte :
// routeur, panier, tenant…).
//
// Robustesse : les thèmes générés par l'IA peuvent contenir une syntaxe
// d'un autre moteur de templates (style Liquid/Shopify). On la neutralise
// pour ne JAMAIS afficher de {{balise}} brute sur le site public :
//   • marqueurs de sections {{#…}} / {{/…}} / {{^…}} / {{else}}, {% … %} → retirés,
//   • balises pointées connues (merchant.name, routes.products…) → résolues,
//   • toute autre balise inconnue (pointée ou non) → retirée silencieusement.

const TAG_REGEX = /\{\{\s*([\w.-]+)((?:\s+[\w-]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s}]+))*)\s*\}\}/g;

interface BlockRef {
  name: string;
  attrs: Record<string, string>;
}

interface BuiltHtml {
  html: string;
  blocks: BlockRef[];
}

/** Balises de blocs gérées par le moteur (mêmes clés que renderBlock). */
const KNOWN_BLOCK_TAGS = new Set([
  'products',
  'services',
  'blog_posts',
  'partners',
  'team',
  'portfolio',
  'contact_form',
  'booking_form',
  'testimonials',
  'stats',
  'marketplace_products',
  'marketplace_sellers',
  'marketplace_search',
  'marketplace_categories',
  'featured_products',
  'faq',
  'gallery',
  'hebergements',
  'hebergement_search',
  'events',
  'formations',
  'categories',
  'restauration',
  'default_form',
  'hero_banners',
]);

/** Correspondance des balises de routes (style Liquid) vers les chemins du thème. */
const ROUTE_ALIASES: Record<string, string> = {
  'routes.root': '/',
  'routes.home': '/',
  'routes.products': '/produits',
  'routes.all_products': '/produits',
  'routes.collections': '/categories',
  'routes.categories': '/categories',
  'routes.services': '/services',
  'routes.cart': '/panier',
  'routes.checkout': '/commande',
  'routes.contact': '/contact',
  'routes.about': '/apropos',
  'routes.blog': '/blog',
  'routes.faq': '/faq',
  'routes.gallery': '/galerie',
  'routes.search': '/recherche',
  'routes.portfolio': '/portfolio',
  'routes.team': '/equipe',
  'routes.partners': '/partenaires',
  'routes.booking': '/booking',
  'routes.account': '/connexion',
};

/** Retire les marqueurs d'un autre moteur de templates (Liquid/Shopify…). */
function stripForeignMarkers(content: string): string {
  return content
    // Balises de contrôle {% … %} (Liquid)
    .replace(/\{%[\s\S]*?%\}/g, '')
    // Ouvertures/fermetures de section {{#bloc}} {{/bloc}} {{^bloc}}
    .replace(/\{\{\s*[#/^][\w.-]*\s*\}\}/g, '')
    // Inversions {{else}} / {{ else }}
    .replace(/\{\{\s*else\s*\}\}/gi, '')
    // Fermetures orphelines éventuelles {{/ … }}
    .replace(/\{\{\s*\/[^}]*\}\}/g, '');
}

function parseAttrs(raw: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /([\w-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s}]+))/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    attrs[m[1]] = (m[2] ?? m[3] ?? m[4] ?? '').trim();
  }
  return attrs;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface TagContext {
  storeName: string;
  storeDesc: string;
  storeLogo: string;
  storePhone: string;
  storeEmail: string;
  storeAddress: string;
  storeCity: string;
  storeCountry: string;
  storeAbout: string;
  storeUrl: string;
  currentYear: number;
}

/**
 * Résout les balises « pointées » (issues d'un autre moteur de templates).
 * Renvoie une chaîne HTML si connue, ou '' pour les neutraliser.
 */
function dottedAlias(name: string, ctx: TagContext): string {
  const lower = name.toLowerCase();

  const route = ROUTE_ALIASES[lower];
  if (route) return escapeHtml(route);

  switch (lower) {
    case 'merchant.name':
    case 'merchant.store_name':
    case 'merchant.shop_name':
    case 'shop.name':
    case 'store.name':
    case 'shopify.shop_name':
      return escapeHtml(ctx.storeName);
    case 'merchant.description':
    case 'shop.description':
    case 'store.description':
      return escapeHtml(ctx.storeDesc);
    case 'merchant.about':
    case 'merchant.about_us':
    case 'merchant.about_title':
    case 'shop.about':
      return escapeHtml(ctx.storeAbout);
    case 'merchant.email':
    case 'shop.email':
    case 'store.email':
      return escapeHtml(ctx.storeEmail);
    case 'merchant.phone':
    case 'merchant.telephone':
    case 'shop.phone':
      return escapeHtml(ctx.storePhone);
    case 'merchant.address':
    case 'shop.address':
      return escapeHtml(ctx.storeAddress);
    case 'merchant.city':
    case 'shop.city':
      return escapeHtml(ctx.storeCity);
    case 'merchant.country':
    case 'shop.country':
      return escapeHtml(ctx.storeCountry);
    case 'merchant.logo':
    case 'shop.logo':
      return ctx.storeLogo
        ? `<img src="${escapeHtml(ctx.storeLogo)}" alt="${escapeHtml(ctx.storeName)}" />`
        : escapeHtml(ctx.storeName);
    case 'site.copyright':
    case 'shop.copyright':
    case 'merchant.copyright':
    case 'store.copyright':
    case 'copyright':
      return escapeHtml(`© ${ctx.currentYear} ${ctx.storeName}`);
    case 'year':
    case 'current_year':
      return String(ctx.currentYear);
    default:
      // Balise pointée d'un autre moteur non reconnue → neutralisée.
      return '';
  }
}

/** Valeur HTML d'une balise simple (ou null si ce n'est pas une balise simple). */
function simpleTagValue(name: string, ctx: TagContext): string | null {
  // Balises pointées : jamais valides dans le moteur Zifek.
  if (name.includes('.')) return dottedAlias(name, ctx);

  switch (name) {
    case 'site_name': return escapeHtml(ctx.storeName);
    case 'site_description': return escapeHtml(ctx.storeDesc);
    case 'site_phone': return escapeHtml(ctx.storePhone);
    case 'site_email': return escapeHtml(ctx.storeEmail);
    case 'site_address': return escapeHtml(ctx.storeAddress);
    case 'site_about': return escapeHtml(ctx.storeAbout);
    case 'site_url': return escapeHtml(ctx.storeUrl);
    case 'year': return String(ctx.currentYear);
    case 'site_logo':
      return ctx.storeLogo
        ? `<img src="${escapeHtml(ctx.storeLogo)}" alt="${escapeHtml(ctx.storeName)}" />`
        : escapeHtml(ctx.storeName);
    default:
      return null;
  }
}

function buildHtml(content: string, ctx: TagContext): BuiltHtml {
  const blocks: BlockRef[] = [];
  const cleaned = stripForeignMarkers(content || '');

  // Un logo placé DANS un attribut (ex. <img src="{{site_logo}}">) doit
  // recevoir l'URL brute du logo, et non un <img> imbriqué (ce qui produisait
  // un src cassé). On le résout donc en priorité dans les attributs.
  const withAttrLogos = cleaned.replace(
    /(src|href|data-src|data-bg|poster)\s*=\s*(["'])\s*\{\{\s*(site_logo|merchant\.logo|shop\.logo|store\.logo)\s*\}\}\s*\2/gi,
    (_m, attr: string, q: string) => `${attr}=${q}${escapeHtml(ctx.storeLogo)}${q}`,
  );

  const html = withAttrLogos.replace(TAG_REGEX, (_full, name: string, rawAttrs: string) => {
    const simple = simpleTagValue(name, ctx);
    if (simple !== null) return simple;
    if (KNOWN_BLOCK_TAGS.has(name)) {
      const idx = blocks.length;
      blocks.push({ name, attrs: parseAttrs(rawAttrs || '') });
      return `<div class="zf-block" data-zf-block="${idx}"></div>`;
    }
    // Balise inconnue → retirée pour ne jamais s'afficher brute.
    return '';
  });
  return { html, blocks };
}

function parseLimit(value: string | undefined, fallback?: number): number | undefined {
  if (value === undefined) return fallback;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
}

function renderBlock(block: BlockRef, key: string): ReactNode {
  const { name, attrs } = block;
  switch (name) {
    case 'products':
      return <ProductsTag key={key} limit={parseLimit(attrs.limit)} />;
    case 'services':
      return <ServicesTag key={key} />;
    case 'blog_posts':
      return <BlogPostsTag key={key} />;
    case 'partners':
      return <PartnersTag key={key} />;
    case 'team':
      return <TeamTag key={key} />;
    case 'portfolio':
      return <PortfolioTag key={key} />;
    case 'contact_form':
      return <ContactFormTag key={key} />;
    case 'booking_form':
      return <BookingFormTag key={key} />;
    case 'testimonials':
      return <TestimonialsTag key={key} />;
    case 'stats':
      return <StatsTag key={key} />;
    case 'marketplace_products':
      return <MarketplaceProductsTag key={key} limit={parseLimit(attrs.limit)} />;
    case 'marketplace_sellers':
      return <MarketplaceSellersTag key={key} limit={parseLimit(attrs.limit)} />;
    case 'marketplace_search':
      return <MarketplaceSearchTag key={key} placeholder={attrs.placeholder} />;
    case 'marketplace_categories':
      return <MarketplaceCategoriesTag key={key} />;
    case 'featured_products':
      return <FeaturedProductsTag key={key} limit={parseLimit(attrs.limit, 6) ?? 6} />;
    case 'faq':
      return <FaqTag key={key} />;
    case 'gallery':
      return <GalleryTag key={key} limit={parseLimit(attrs.limit)} />;
    case 'hebergements':
      return <HebergementsTag key={key} limit={parseLimit(attrs.limit)} />;
    case 'hebergement_search':
      return <HebergementSearchTag key={key} limit={parseLimit(attrs.limit)} />;
    case 'events':
      return <EventsTag key={key} limit={parseLimit(attrs.limit)} />;
    case 'formations':
      return <FormationsTag key={key} limit={parseLimit(attrs.limit)} />;
    case 'categories':
      return <CategoriesTag key={key} />;
    case 'restauration':
      return <RestaurationTag key={key} limit={parseLimit(attrs.limit)} />;
    case 'default_form':
      return <DefaultFormTag key={key} />;
    case 'hero_banners':
      return (
        <HeroBannersTag
          key={key}
          limit={parseLimit(attrs.limit)}
          index={parseLimit(attrs.index)}
          banner={parseLimit(attrs.banner)}
        />
      );
    default:
      return null;
  }
}

interface TemplateRendererProps {
  content: string;
}

export default function TemplateRenderer({ content }: TemplateRendererProps) {
  const { tenant, theme } = useTenant();
  const containerRef = useRef<HTMLDivElement>(null);
  const [mounts, setMounts] = useState<{ idx: number; node: Element }[]>([]);

  // Moteur « React » : si le contenu de la page est un schéma de blocs JSON,
  // on le rend avec de vrais composants React au lieu du HTML classique.
  const blockSchema = useMemo(() => parseBlockSchema(content || ''), [content]);

  const built = useMemo<BuiltHtml>(() => {
    if (blockSchema) return { html: '', blocks: [] };
    const ctx: TagContext = {
      storeName: resolveStoreName(tenant, theme),
      storeDesc: htmlToText(tenant?.description || tenant?.aboutus),
      storeLogo: resolveMediaUrl(tenant?.image || theme?.navImage || ''),
      storePhone: tenant?.telephone || '',
      storeEmail: tenant?.email || '',
      storeAddress: [tenant?.adresse, tenant?.Quartier, tenant?.Ville, tenant?.Pays].filter(Boolean).join(', '),
      storeCity: tenant?.Ville || '',
      storeCountry: tenant?.Pays || '',
      storeAbout: htmlToText(tenant?.aboutus) || htmlToText(tenant?.description),
      storeUrl: typeof window !== 'undefined' ? window.location.origin : '',
      currentYear: new Date().getFullYear(),
    };
    return buildHtml(content || '', ctx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, tenant, theme, blockSchema]);

  // Après injection du HTML, on repère les emplacements de blocs et on y
  // monte les composants React correspondants.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      setMounts([]);
      return;
    }
    const nodes = Array.from(el.querySelectorAll('[data-zf-block]'));
    setMounts(
      nodes.map((node) => ({ idx: Number((node as HTMLElement).dataset.zfBlock), node })),
    );
  }, [built.html]);

  if (blockSchema) {
    return (
      <div className="zifek-theme-scope">
        <ThemeBlockRenderer schema={blockSchema} />
      </div>
    );
  }

  return (
    <>
      <div ref={containerRef} className="zifek-theme-scope" dangerouslySetInnerHTML={{ __html: built.html }} />
      {mounts.map(({ idx, node }) => {
        const block = built.blocks[idx];
        if (!block) return null;
        return createPortal(renderBlock(block, `zf-${built.html.length}-${idx}`), node, `${built.html.length}-${idx}`);
      })}
    </>
  );
}